import { useRef } from "react";
import { useMemo, useCallback } from 'react';
import * as THREE from 'three';
import { DataPoint } from './MarketDataGenerator';
import { DBG_ENABLED, dbgScaleCalc } from './debugTelemetry';
import { syncCount, SC } from './syncProbe';

export interface WaveGeometryLayout {
  width3D: number;
  heightScale3D: number;
  zLimit3D: number;
  startX: number;
  floorDistance3D?: number;
  sideGraphDistance3D?: number;
}

export interface WaveGeometryConfig {
  momentumSensitivity: number;
  curveTension: number;
  historyShadowStrength: number;
  sideHistoryShadowStrength: number;
  sma10Period: number;
  sma20Period: number;
  sma50Period: number;
  showSMA10: boolean;
  showSMA20: boolean;
  showSMA50: boolean;
  fullColors3D: Float32Array;
}

/** Amortecimento do desenho das SMAs no filamento (mesmo para Y e Z); Z usa este valor × 1,2. */
export function smaDamping(period: number): number {
  if (period <= 14) return 1.0;
  if (period >= 200) return 0.35;
  return 1.0 - (period - 14) * (0.65 / (200 - 14));
}

export function useWaveGeometry(
  data: DataPoint[],
  layout: WaveGeometryLayout,
  config: WaveGeometryConfig,
  dbgTag: number = -1 // só diagnóstico (?dbg=1): 0 = lote principal, 1 = parede; -1 = não contar
) {
  if (dbgTag >= 0) syncCount(dbgTag === 0 ? SC.WAVE_CALL_MAIN : SC.WAVE_CALL_WALL);
  // 1. Calculate boundaries
  const { minP, maxP, maxMom } = useMemo(() => {
    const tCalc = DBG_ENABLED ? performance.now() : 0;
    let minPrice = Infinity;
    let maxPrice = -Infinity;
    
    if (data.length === 0) return { minP: 0, maxP: 100, maxMom: 1 };
    
    for (let i = 0; i < data.length; i++) {
      const val = data[i].close ?? data[i].price;
      if (val !== undefined && !isNaN(val)) {
        if (val < minPrice) minPrice = val;
        if (val > maxPrice) maxPrice = val;
      }
      
      const s10 = (data[i] as any).sma10;
      const s20 = (data[i] as any).sma20;
      const s50 = (data[i] as any).sma50;
      
      if (config.showSMA10 && s10 !== undefined && !isNaN(s10)) {
        if (s10 < minPrice) minPrice = s10;
        if (s10 > maxPrice) maxPrice = s10;
      }
      if (config.showSMA20 && s20 !== undefined && !isNaN(s20)) {
        if (s20 < minPrice) minPrice = s20;
        if (s20 > maxPrice) maxPrice = s20;
      }
      if (config.showSMA50 && s50 !== undefined && !isNaN(s50)) {
        if (s50 < minPrice) minPrice = s50;
        if (s50 > maxPrice) maxPrice = s50;
      }
    }
    
    if (minPrice === Infinity || isNaN(minPrice)) minPrice = 10;
    if (maxPrice === -Infinity || isNaN(maxPrice)) maxPrice = 180;
    
    const priceRange = Math.max(0.0001, maxPrice - minPrice);
    const pPadding = priceRange * 0.15;
    const computedMinP = minPrice - pPadding;
    const computedMaxP = maxPrice + pPadding;
    
    const magnitudes = [];
    for (let i = 0; i < data.length; i++) {
      magnitudes.push(Math.abs(data[i].momentum || 0));
      magnitudes.push(Math.abs(data[i].imf || 0));
    }
    magnitudes.sort((a, b) => a - b);
    const p99 = magnitudes[Math.floor(magnitudes.length * 0.995)] || 0;
    const computedMaxMom = p99 === 0 ? 1 : p99;
    
    if (DBG_ENABLED) dbgScaleCalc(performance.now() - tCalc);
    return { minP: computedMinP, maxP: computedMaxP, maxMom: computedMaxMom };
  }, [data, config.showSMA10, config.showSMA20, config.showSMA50]);

  // 2. Define coordinate transformations
  const { width3D, heightScale3D, zLimit3D, startX } = layout;
  
  const getY = useCallback((price: number) => {
    return ((price - minP) / (maxP - minP)) * heightScale3D - (heightScale3D / 2);
  }, [minP, maxP, heightScale3D]);
  
  const getPrice = useCallback((y: number) => {
    return ((y + (heightScale3D / 2)) / heightScale3D) * (maxP - minP) + minP;
  }, [minP, maxP, heightScale3D]);
  
  const getTime = useCallback((x: number) => (x - startX) / width3D, [startX, width3D]);
  
  const getSmaDamping = useCallback((key: string) => {
    let period = 20;
    if (key === 'sma10') period = config.sma10Period;
    else if (key === 'sma20') period = config.sma20Period;
    else if (key === 'sma50') period = config.sma50Period;
    
    return smaDamping(period);
  }, [config.sma10Period, config.sma20Period, config.sma50Period]);
  
  const priceToPixelScale = heightScale3D / Math.max(maxP - minP, 0.0001);
  const targetZScale = priceToPixelScale * config.momentumSensitivity;
  const baseMaxZAmplitude = zLimit3D / 1.15;
  const maxAllowedZScale = maxMom > 0 ? (baseMaxZAmplitude / maxMom) : 1;
  const zScale = Math.min(maxAllowedZScale, targetZScale);
  
  const getHelixY = useCallback((d: any) => {
    const trendY = getY(d.trend || d.price);
    const imfOffset = (d.imf || 0) * priceToPixelScale;
    return trendY + imfOffset;
  }, [getY, priceToPixelScale]);
  
  const getHelixZ = useCallback((d: any) => {
    const val = d.momentum || 0;
    return val * zScale;
  }, [zScale]);
  
  const getSmaHelixY = useCallback((d: any, key: string) => {
    const smaY = getY(d[key] || d.price);
    const imfKey = `${key}Imf`;
    const imfValue = d[imfKey] || 0;
    const damping = getSmaDamping(key);
    const yDamping = damping * 0.04;
    const imfOffset = imfValue * priceToPixelScale * yDamping;
    return smaY + imfOffset;
  }, [getY, getSmaDamping, priceToPixelScale]);
  
  const getSmaHelixZ = useCallback((d: any, key: string) => {
    const momKey = `${key}Momentum`;
    const momValue = d[momKey] || 0;
    const damping = getSmaDamping(key);
    const zDamping = damping * 1.2;
    const momOffset = momValue * zScale * zDamping;
    return momOffset;
  }, [getSmaDamping, zScale]);

  // Buffer refs for performance optimization
  const decimatedPtsRef = useRef<Float32Array>(new Float32Array(3000));
  const decimatedColsRef = useRef<Float32Array>(new Float32Array(3000));
  const smoothedPtsRef = useRef<Float32Array>(new Float32Array(3000));
  const smoothedCols3DRef = useRef<Float32Array>(new Float32Array(3000));
  const smoothedColsSideRef = useRef<Float32Array>(new Float32Array(3000));
  const smoothedColsFloorRef = useRef<Float32Array>(new Float32Array(3000));
  const vectorPoolRef = useRef<THREE.Vector3[]>([]);

  const cachedDecimatedLen = useRef<number>(0);
  const cachedSmoothedLen = useRef<number>(0);

  // 3. Compute Decimated Points with Live-Tip Optimization
  const { decimatedPoints, decimatedColors, finalLivePos, finalLiveColor, finalColorRGB, decimatedPtsCount } = useMemo(() => {
    if (dbgTag >= 0) syncCount(dbgTag === 0 ? SC.WAVE_REBUILD_MAIN : SC.WAVE_REBUILD_WALL);
    if (data.length === 0) return { decimatedPoints: new Float32Array(0), decimatedColors: new Float32Array(0), finalLivePos: [0,0,0] as [number,number,number], finalLiveColor: [1,1,1] as [number,number,number], finalColorRGB: 'rgb(255,255,255)', decimatedPtsCount: 0 };
    
    let requiredLength = data.length * 3;
    if (requiredLength > decimatedPtsRef.current.length) {
      let newCap = decimatedPtsRef.current.length * 2;
      while (newCap < requiredLength) newCap *= 2;
      decimatedPtsRef.current = new Float32Array(newCap);
      decimatedColsRef.current = new Float32Array(newCap);
    }
    
    const pBuf = decimatedPtsRef.current;
    const cBuf = decimatedColsRef.current;
    
    const minDistanceSq = 0.00001;
    let lastX = -99999, lastY = -99999, lastZ = -99999;
    let ptCount = 0;
    
    for (let i=0; i<data.length; i++) {
      const d = data[i];
      const px = startX + (i / Math.max(1, (data.length - 1))) * width3D;
      const py = getHelixY(d);
      const pz = getHelixZ(d);
      
      if (i === data.length - 1) {
        const idx = ptCount * 3;
        pBuf[idx] = px; pBuf[idx+1] = py; pBuf[idx+2] = pz;
        const colIdx = i * 3;
        cBuf[idx] = config.fullColors3D[colIdx] ?? 1;
        cBuf[idx+1] = config.fullColors3D[colIdx+1] ?? 1;
        cBuf[idx+2] = config.fullColors3D[colIdx+2] ?? 1;
        ptCount++;
        break;
      }
      
      const dx = px - lastX;
      const dy = py - lastY;
      const dz = pz - lastZ;
      if ((dx*dx + dy*dy + dz*dz) > minDistanceSq) {
        const idx = ptCount * 3;
        pBuf[idx] = px; pBuf[idx+1] = py; pBuf[idx+2] = pz;
        const colIdx = i * 3;
        cBuf[idx] = config.fullColors3D[colIdx] ?? 1;
        cBuf[idx+1] = config.fullColors3D[colIdx+1] ?? 1;
        cBuf[idx+2] = config.fullColors3D[colIdx+2] ?? 1;
        lastX = px; lastY = py; lastZ = pz;
        ptCount++;
      }
    }
    
    cachedDecimatedLen.current = ptCount;
    const lastIdx = (ptCount - 1) * 3;
    const lastR = cBuf[lastIdx] ?? 1;
    const lastG = cBuf[lastIdx+1] ?? 1;
    const lastB = cBuf[lastIdx+2] ?? 1;
    
    const flp: [number,number,number] = [pBuf[lastIdx], pBuf[lastIdx+1], pBuf[lastIdx+2]];
    const fcRGB = `rgb(${Math.round(lastR*255)},${Math.round(lastG*255)},${Math.round(lastB*255)})`;
    
    return { 
      decimatedPoints: pBuf.subarray(0, ptCount * 3), 
      decimatedColors: cBuf.subarray(0, ptCount * 3), 
      finalLivePos: flp, 
      finalLiveColor: [lastR, lastG, lastB] as [number, number, number], 
      finalColorRGB: fcRGB,
      decimatedPtsCount: ptCount
    };
  }, [data, minP, maxP, width3D, startX, config.fullColors3D, getHelixY, getHelixZ]);

  // 4. Compute Smoothed Spline Points (Heavy Catmull-Rom generation only when count/tension changes)
  const { smoothedPoints, smoothedColors3D, smoothedColorsSide, smoothedColorsFloor, smoothedPtsCount } = useMemo(() => {
    if (decimatedPtsCount < 2) {
      return { smoothedPoints: new Float32Array(0), smoothedColors3D: new Float32Array(0), smoothedColorsSide: new Float32Array(0), smoothedColorsFloor: new Float32Array(0), smoothedPtsCount: 0 };
    }

    if (decimatedPtsCount > 250) {
      const n = decimatedPtsCount;
      const requiredLength = n * 3;
      
      if (requiredLength > smoothedPtsRef.current.length) {
        let newCap = smoothedPtsRef.current.length * 2;
        while (newCap < requiredLength) newCap *= 2;
        smoothedPtsRef.current = new Float32Array(newCap);
        smoothedCols3DRef.current = new Float32Array(newCap);
        smoothedColsSideRef.current = new Float32Array(newCap);
        smoothedColsFloorRef.current = new Float32Array(newCap);
      }
      
      const ptsOut = smoothedPtsRef.current;
      const col3DOut = smoothedCols3DRef.current;
      const colSideOut = smoothedColsSideRef.current;
      const colFloorOut = smoothedColsFloorRef.current;
      
      for (let i = 0; i < n; i++) {
        const idx = i * 3;
        ptsOut[idx] = decimatedPoints[idx];
        ptsOut[idx+1] = decimatedPoints[idx+1];
        ptsOut[idx+2] = decimatedPoints[idx+2];
        
        const u = i / (n - 1);
        const r = decimatedColors[idx];
        const g = decimatedColors[idx+1];
        const b = decimatedColors[idx+2];
        
        const maxFade = 1.0;
        const minFade = maxFade * Math.max(0.2, 1 - config.historyShadowStrength);
        const historyFade3D = u < 0.8 ? minFade + ((maxFade - minFade) * (u / 0.8)) : maxFade;
        col3DOut[idx] = r * historyFade3D;
        col3DOut[idx+1] = g * historyFade3D;
        col3DOut[idx+2] = b * historyFade3D;
        
        const minFadeSide = maxFade * Math.max(0.2, 1 - config.sideHistoryShadowStrength);
        const historyFadeSide = u < 0.8 ? minFadeSide + ((maxFade - minFadeSide) * (u / 0.8)) : maxFade;
        colSideOut[idx] = r * historyFadeSide;
        colSideOut[idx+1] = g * historyFadeSide;
        colSideOut[idx+2] = b * historyFadeSide;
        
        colFloorOut[idx] = r * maxFade;
        colFloorOut[idx+1] = g * maxFade;
        colFloorOut[idx+2] = b * maxFade;
      }
      
      cachedSmoothedLen.current = n;
      return { 
        smoothedPoints: ptsOut.subarray(0, requiredLength), 
        smoothedColors3D: col3DOut.subarray(0, requiredLength), 
        smoothedColorsSide: colSideOut.subarray(0, requiredLength), 
        smoothedColorsFloor: colFloorOut.subarray(0, requiredLength),
        smoothedPtsCount: n
      };
    }

    // High fidelity CatmullRom Curve for lower sample counts
    const tempVecs: THREE.Vector3[] = [];
    for (let i = 0; i < decimatedPtsCount; i++) {
       if (i < vectorPoolRef.current.length) {
         vectorPoolRef.current[i].set(decimatedPoints[i*3], decimatedPoints[i*3+1], decimatedPoints[i*3+2]);
       } else {
         vectorPoolRef.current.push(new THREE.Vector3(decimatedPoints[i*3], decimatedPoints[i*3+1], decimatedPoints[i*3+2]));
       }
       tempVecs.push(vectorPoolRef.current[i]);
    }
    const curve = new THREE.CatmullRomCurve3(tempVecs, false, 'catmullrom', config.curveTension);
    const totalSamples = Math.min(500, decimatedPtsCount * 2);
    const n = totalSamples + 1;
    const requiredLength = n * 3;
    
    if (requiredLength > smoothedPtsRef.current.length) {
      let newCap = smoothedPtsRef.current.length * 2;
      while (newCap < requiredLength) newCap *= 2;
      smoothedPtsRef.current = new Float32Array(newCap);
      smoothedCols3DRef.current = new Float32Array(newCap);
      smoothedColsSideRef.current = new Float32Array(newCap);
      smoothedColsFloorRef.current = new Float32Array(newCap);
    }
    
    const ptsOut = smoothedPtsRef.current;
    const col3DOut = smoothedCols3DRef.current;
    const colSideOut = smoothedColsSideRef.current;
    const colFloorOut = smoothedColsFloorRef.current;
    const targetVec = new THREE.Vector3();
    
    for (let i = 0; i <= totalSamples; i++) {
      const u = i / totalSamples;
      curve.getPoint(u, targetVec);
      const idx = i * 3;
      
      ptsOut[idx] = targetVec.x;
      ptsOut[idx+1] = targetVec.y;
      ptsOut[idx+2] = targetVec.z;
      
      const numColors = Math.floor(decimatedColors.length / 3);
      const colorIdx = Math.min(numColors - 1, Math.floor(u * (numColors - 1))) * 3;
      const r = decimatedColors[colorIdx] ?? 1;
      const g = decimatedColors[colorIdx+1] ?? 1;
      const b = decimatedColors[colorIdx+2] ?? 1;
      
      const maxFade = 1.0;
      const minFade = maxFade * Math.max(0.2, 1 - config.historyShadowStrength);
      const historyFade3D = u < 0.8 ? minFade + ((maxFade - minFade) * (u / 0.8)) : maxFade;
      
      col3DOut[idx] = r * historyFade3D;
      col3DOut[idx+1] = g * historyFade3D;
      col3DOut[idx+2] = b * historyFade3D;
      
      const minFadeSide = maxFade * Math.max(0.2, 1 - config.sideHistoryShadowStrength);
      const historyFadeSide = u < 0.8 ? minFadeSide + ((maxFade - minFadeSide) * (u / 0.8)) : maxFade;
      
      colSideOut[idx] = r * historyFadeSide;
      colSideOut[idx+1] = g * historyFadeSide;
      colSideOut[idx+2] = b * historyFadeSide;
      
      colFloorOut[idx] = r * maxFade;
      colFloorOut[idx+1] = g * maxFade;
      colFloorOut[idx+2] = b * maxFade;
    }
    
    cachedSmoothedLen.current = n;
    return { 
      smoothedPoints: ptsOut.subarray(0, requiredLength), 
      smoothedColors3D: col3DOut.subarray(0, requiredLength), 
      smoothedColorsSide: colSideOut.subarray(0, requiredLength), 
      smoothedColorsFloor: colFloorOut.subarray(0, requiredLength),
      smoothedPtsCount: n
    };
  }, [decimatedPoints, decimatedColors, decimatedPtsCount, config.curveTension, config.historyShadowStrength, config.sideHistoryShadowStrength]);

  return {
    minP, maxP, maxMom, priceToPixelScale, zScale,
    getY, getPrice, getTime, getHelixY, getHelixZ, getSmaHelixY, getSmaHelixZ,
    decimatedPoints, decimatedColors, finalLivePos, finalLiveColor, finalColorRGB,
    smoothedPoints, smoothedColors3D, smoothedColorsSide, smoothedColorsFloor,
    smoothedPtsCount
  };
}

export function useWaveColors(
  data: DataPoint[],
  colorUp: string,
  colorDown: string,
  colorIntensity: number,
  colorContrast: number
) {
  const colorsRef = useRef<Float32Array>(new Float32Array(3000));
  const getAdjustedColor = (hex: string, isUp: boolean) => {
    const color = new THREE.Color(hex);
    const hsl = { h: 0, s: 0, l: 0 };
    color.getHSL(hsl);
    color.setHSL(hsl.h, Math.min(1, hsl.s * colorContrast * 0.7), Math.min(1, hsl.l * (isUp ? 1.0 : 0.85)));
    color.multiplyScalar(colorIntensity * 0.85);
    return color;
  };

  const adjustedColorUp = useMemo(() => getAdjustedColor(colorUp, true), [colorUp, colorIntensity, colorContrast]);
  const adjustedColorDown = useMemo(() => getAdjustedColor(colorDown, false), [colorDown, colorIntensity, colorContrast]);
  const adjustedColorNeutral = useMemo(() => {
    const c = new THREE.Color("#6b7280");
    c.multiplyScalar(colorIntensity * 0.7);
    return c;
  }, [colorIntensity]);

  const fullColors3D = useMemo(() => {
    const requiredLength = data.length * 3;
    if (requiredLength > colorsRef.current.length) {
      let newCap = colorsRef.current.length * 2;
      while (newCap < requiredLength) newCap *= 2;
      colorsRef.current = new Float32Array(newCap);
    }
    const arr = colorsRef.current;
    
    for (let i = 0; i < data.length; i++) {
      const d = data[i];
      let isUp = true;
      let isConsolidation = false;
      if (d.open !== undefined) {
         const body = Math.abs(d.close! - d.open!);
         const range = d.high! - d.low!;
         if (range > 0 && body / range < 0.2) isConsolidation = true;
         isUp = d.close! >= d.open!;
      } else {
         if (i > 0) {
             const delta = Math.abs(d.price - data[i-1].price);
             if (delta < d.price * 0.00015) isConsolidation = true;
             isUp = d.price >= data[i-1].price;
         }
      }
      const color = isConsolidation ? adjustedColorNeutral : (isUp ? adjustedColorUp : adjustedColorDown);
      const idx = i * 3;
      arr[idx] = color.r;
      arr[idx+1] = color.g;
      arr[idx+2] = color.b;
    }
    return arr.subarray(0, requiredLength);
  }, [data, adjustedColorUp, adjustedColorDown, adjustedColorNeutral]);

  return { fullColors3D, adjustedColorUp, adjustedColorDown, adjustedColorNeutral };
}
